import React, { useEffect, useState } from 'react';
import { Loader2, Activity, Clock, ShieldCheck, TrendingUp, Zap, BarChart3, Code, ShipWheel } from 'lucide-react';
import { ModeToggle } from '@/components/mode-toggle';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '@/components/theme-provider';
import { apiClient, type WorkflowObservabilityData } from '../api/client';
import { formatInternalName } from '../utils/formatInternalName';
import slackColorIcon from '../assets/icons/slack-color.png';
import pagerdutyColorIcon from '../assets/icons/pagerduty-icon.svg';
import confluenceIcon from '../assets/icons/confluence.svg';
import jiraIcon from '../assets/icons/jira.svg';
import linearIcon from '../assets/icons/linear.png';
import linearDarkIcon from '../assets/icons/linear-dark-mode.png';
import datadogLightIcon from '../assets/icons/datadog-light-mode.svg';
import datadogDarkIcon from '../assets/icons/datadog-dark-mode.png';
import gitlabIcon from '../assets/icons/gitlab-clean.svg';
import kestrelLightLogo from '../assets/icons/kestrel-light.svg';
import kestrelDarkLogo from '../assets/icons/kestrel-dark.svg';
import argocdIcon from '../assets/icons/argocd.svg';
import fluxcdIcon from '../assets/icons/flux-cd.svg';
import karpenterIcon from '../assets/icons/karpenter.svg';
import kyvernoIcon from '../assets/icons/kyverno.svg';
import trivyIcon from '../assets/icons/trivy.svg';
import helmLightIcon from '../assets/icons/helm-light.svg';
import helmDarkIcon from '../assets/icons/helm-dark.svg';
import posthogIcon from '../assets/icons/posthog-logo-stacked.svg';
import posthogLightIcon from '../assets/icons/posthog-light.png';
import vercelIcon from '../assets/icons/vercel.svg';
import vercelLightIcon from '../assets/icons/vercel-light.svg';
import railwayIcon from '../assets/icons/railway.svg';
import flyioIcon from '../assets/icons/flyio.svg';
import nebiusIcon from '../assets/icons/NBIS.svg';
import { ReactComponent as BeamIcon } from '../assets/icons/beam.svg';
import { ReactComponent as DaytonaIcon } from '../assets/icons/daytona.svg';
import supabaseIcon from '../assets/icons/supabase-logo-icon.svg';
import { ReactComponent as PlanetScaleIcon } from '../assets/icons/planetscale-mono.svg';
import neonIcon from '../assets/icons/neon-logomark-light-color.svg';
import clickhouseIcon from '../assets/icons/clickhouse.svg';
import jenkinsIcon from '../assets/icons/Jenkins.svg';
import { ReactComponent as CircleCIIcon } from '../assets/icons/circleci-mono.svg';
import { ReactComponent as SonarCloudIcon } from '../assets/icons/sonarcloud-mono.svg';
import { ReactComponent as OktaIcon } from '../assets/icons/okta-mono.svg';
import databricksIcon from '../assets/icons/databricks.png';
import { ReactComponent as GCPObsIcon } from '../assets/icons/gcp.svg';
import terraformIcon from '../assets/icons/HashiCorp Terraform.svg';
import pulumiIcon from '../assets/icons/pulumi-logo-mark-color.svg';
import vaultIcon from '../assets/icons/hashicorp-vault.svg';
import infisicalIcon from '../assets/icons/infisical.svg';
import cloudflareIcon from '../assets/icons/cloudflare.svg';
import awsLightLogo from '../assets/icons/aws.svg';
import awsDarkLogo from '../assets/icons/aws-dark.svg';

const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#14b8a6', '#6366f1'];

function formatDuration(sec: number): string {
  if (sec < 60) return `${Math.round(sec)}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ${Math.round((sec % 3600) / 60)}m`;
  const days = Math.floor(sec / 86400);
  const hours = Math.round((sec % 86400) / 3600);
  return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00Z');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function IntegrationIcon({ integration, isDark }: { integration: string; isDark: boolean }) {
  const cls = 'w-4 h-4';
  const id = integration.toLowerCase();
  switch (id) {
    case 'kestrel': return <img src={isDark ? kestrelDarkLogo : kestrelLightLogo} alt="Kestrel" className={cls} />;
    case 'github': return <img src={isDark ? 'https://github.githubassets.com/favicons/favicon-dark.svg' : 'https://github.githubassets.com/favicons/favicon.svg'} alt="GitHub" className={cls} />;
    case 'gitlab': return <img src={gitlabIcon} alt="GitLab" className={cls} />;
    case 'slack': return <img src={slackColorIcon} alt="Slack" className={cls} />;
    case 'pagerduty': return <img src={pagerdutyColorIcon} alt="PagerDuty" className={cls} />;
    case 'confluence': return <img src={confluenceIcon} alt="Confluence" className={cls} />;
    case 'jira': return <img src={jiraIcon} alt="Jira" className={cls} />;
    case 'linear': return <img src={isDark ? linearDarkIcon : linearIcon} alt="Linear" className={cls} />;
    case 'datadog': return <img src={isDark ? datadogDarkIcon : datadogLightIcon} alt="Datadog" className={cls} />;
    case 'argocd': return <img src={argocdIcon} alt="ArgoCD" className={cls} />;
    case 'argo-rollouts': return <img src={argocdIcon} alt="Argo Rollouts" className={cls} />;
    case 'fluxcd': return <img src={fluxcdIcon} alt="Flux CD" className={cls} />;
    case 'karpenter': return <img src={karpenterIcon} alt="Karpenter" className={cls} />;
    case 'kyverno': return <img src={kyvernoIcon} alt="Kyverno" className={cls} />;
    case 'trivy': return <img src={trivyIcon} alt="Trivy" className={cls} />;
    case 'helm': return <img src={isDark ? helmDarkIcon : helmLightIcon} alt="Helm" className={cls} />;
    case 'posthog': return <img src={isDark ? posthogLightIcon : posthogIcon} alt="PostHog" className={`${cls} object-contain`} />;
    case 'vercel': return <img src={isDark ? vercelLightIcon : vercelIcon} alt="Vercel" className={cls} />;
    case 'railway': return <img src={railwayIcon} alt="Railway" className={`${cls} object-contain`} style={{ filter: isDark ? 'invert(1)' : 'none' }} />;
    case 'flyio': return <img src={flyioIcon} alt="Fly.io" className={`${cls} object-contain`} />;
    case 'nebius': return <img src={nebiusIcon} alt="Nebius" className={`${cls} object-contain`} />;
    case 'beam': return <BeamIcon className={`${cls} text-foreground`} />;
    case 'daytona': return <DaytonaIcon className={`${cls} text-foreground`} />;
    case 'supabase': return <img src={supabaseIcon} alt="Supabase" className={`${cls} object-contain`} />;
    case 'planetscale': return <PlanetScaleIcon className={`${cls} text-foreground`} />;
    case 'neon': return <img src={neonIcon} alt="Neon" className={`${cls} object-contain`} />;
    case 'clickhouse': return <img src={clickhouseIcon} alt="ClickHouse" className={`${cls} object-contain`} />;
    case 'jenkins': return <img src={jenkinsIcon} alt="Jenkins" className={`${cls} object-contain`} />;
    case 'circleci': return <CircleCIIcon className={`${cls} text-foreground`} />;
    case 'sonarcloud': return <SonarCloudIcon className={`${cls} text-foreground`} />;
    case 'okta': return <OktaIcon className={`${cls} text-foreground`} />;
    case 'databricks': return <img src={databricksIcon} alt="Databricks" className={`${cls} object-contain`} />;
    case 'gcp':
    case 'gcp-security':
    case 'gcp-data':
    case 'gcp-cost': return <GCPObsIcon className={`${cls} object-contain`} />;
    case 'terraform': return <img src={terraformIcon} alt="Terraform" className={`${cls} object-contain`} />;
    case 'pulumi': return <img src={pulumiIcon} alt="Pulumi" className={`${cls} object-contain`} />;
    case 'vault': return <img src={vaultIcon} alt="Vault" className={`${cls} object-contain`} />;
    case 'infisical': return <img src={infisicalIcon} alt="Infisical" className={`${cls} object-contain rounded-sm`} />;
    case 'cloudflare': return <img src={cloudflareIcon} alt="Cloudflare" className={cls} />;
    case 'aws-cost': case 'aws cost': case 'cloud-cost': case 'cloud cost': case 'aws': return <img src={isDark ? awsDarkLogo : awsLightLogo} alt="AWS" className={cls} />;
    case 'kubernetes': return <ShipWheel className={`${cls} text-blue-500`} />;
    default: return <Code className={cls + ' text-muted-foreground'} />;
  }
}

function errorRateColor(rate: number): string {
  if (rate >= 50) return 'text-red-500 font-semibold';
  if (rate >= 25) return 'text-orange-500 font-semibold';
  if (rate >= 1) return 'text-yellow-500';
  return 'text-green-500';
}

function StatCard({ label, value, subtext, icon: Icon }: { label: string; value: string; subtext?: string; icon: React.ElementType }) {
  return (
    <div className="bg-card border rounded-xl p-5 flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="text-2xl font-bold tracking-tight">{value}</div>
      {subtext && <span className="text-xs text-muted-foreground">{subtext}</span>}
    </div>
  );
}

function ChartCard({ title, children, className = '' }: { title: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-card border rounded-xl p-5 ${className}`}>
      <h3 className="text-sm font-semibold mb-4">{title}</h3>
      {children}
    </div>
  );
}

function TruncatedTick({ x, y, payload, width: axisWidth }: any) {
  const w = axisWidth || 140;
  const maxChars = Math.max(10, Math.floor(w / 6.5));
  const text = payload?.value || '';
  const truncated = text.length > maxChars ? text.slice(0, maxChars - 1) + '…' : text;
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={-4} y={0} dy={4} textAnchor="end" fill="hsl(var(--muted-foreground))" fontSize={11}>
        {truncated}
      </text>
    </g>
  );
}

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const nonZero = payload.filter((p: any) => p.value !== 0 && p.value != null);
  if (nonZero.length === 0) return null;
  return (
    <div className="bg-popover border rounded-lg shadow-lg px-3 py-2 text-xs max-w-xs">
      <div className="font-semibold mb-1 break-words">{label}</div>
      {nonZero.map((p: any, i: number) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
          <span className="text-muted-foreground truncate">{p.name}</span>
          <span className="font-medium ml-auto shrink-0">{typeof p.value === 'number' && p.value % 1 !== 0 ? p.value.toFixed(1) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

function DurationTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border rounded-lg shadow-lg px-3 py-2 text-xs max-w-xs">
      <div className="font-semibold mb-1 break-words">{label}</div>
      {payload.map((p: any, i: number) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
          <span className="text-muted-foreground truncate">{p.name}</span>
          <span className="font-medium ml-auto shrink-0">{formatDuration(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

const WorkflowObservability: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const [data, setData] = useState<WorkflowObservabilityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [integrationPage, setIntegrationPage] = useState(0);
  const [durationPage, setDurationPage] = useState(0);

  useEffect(() => {
    if (!authLoading && user) {
      apiClient.getWorkflowObservability()
        .then(setData)
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [authLoading, user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-24">
        <p className="text-sm text-muted-foreground">No observability data available.</p>
      </div>
    );
  }

  // Prepare per-workflow daily data as pivoted for multi-line chart
  const dailyWorkflowMap = new Map<string, Record<string, number>>();
  const workflowNames = new Map<string, string>();
  (data.daily_per_workflow || []).forEach(d => {
    const existing = dailyWorkflowMap.get(d.date) || {};
    existing[d.workflow_id] = (existing[d.workflow_id] || 0) + d.count;
    dailyWorkflowMap.set(d.date, existing);
    workflowNames.set(d.workflow_id, d.workflow_name);
  });
  const dailyChartData = Array.from(dailyWorkflowMap.entries())
    .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
    .map(([date, counts]) => {
      const row: Record<string, any> = { date: formatShortDate(date) };
      for (const wfId of Array.from(workflowNames.keys())) {
        row[wfId] = counts[wfId] || 0;
      }
      return row;
    });
  const uniqueWorkflowIds = Array.from(workflowNames.keys());

  // Per-workflow stacked bar data
  const perWorkflowBarData = (data.per_workflow || []).map(w => ({
    name: w.workflow_name,
    completed: w.completed,
    failed: w.failed,
    waiting: w.waiting,
  }));

  // Failure rate bar data
  const failureRateData = (data.per_workflow || [])
    .filter(w => w.completed + w.failed > 0)
    .sort((a, b) => b.failure_rate - a.failure_rate)
    .map(w => ({
      name: w.workflow_name,
      rate: Math.round(w.failure_rate * 10) / 10,
    }));

  // Duration chart data
  const durationData = (data.duration_by_workflow || []).map(d => ({
    name: d.workflow_name,
    avg: Math.round(d.avg_sec * 10) / 10,
    p95: Math.round(d.p95_sec * 10) / 10,
  }));

  // Integration invocations
  const integrationData = (data.integration_invocations || []).map(i => ({
    name: formatInternalName(i.integration || 'unknown'),
    count: i.count,
    errorRate: Math.round((i.error_rate || 0) * 10) / 10,
  }));

  // Daily approval outcomes
  const approvalOutcomeData = (data.daily_approval_outcomes || []).map(d => ({
    date: formatShortDate(d.date),
    approved: d.approved,
    rejected: d.rejected,
  }));

  // Daily requests
  const requestData = (data.daily_requests || []).map(d => ({
    date: formatShortDate(d.date),
    Slack: d.slack,
    CLI: d.cli,
    Platform: d.platform,
  }));

  // Trigger sources for donut
  const triggerSourceData = (data.trigger_sources || []).map(t => ({
    name: t.source,
    value: t.count,
  }));

  // Request categories for donut
  const MAX_LEGEND_ITEMS = 8;
  const categoryDataRaw = (data.request_categories || []).map(c => ({
    name: formatInternalName(c.category) || 'Uncategorized',
    value: c.count,
  }));
  const categoryData = categoryDataRaw.length > MAX_LEGEND_ITEMS
    ? [
        ...categoryDataRaw.slice(0, MAX_LEGEND_ITEMS - 1),
        { name: 'Other', value: categoryDataRaw.slice(MAX_LEGEND_ITEMS - 1).reduce((s, c) => s + c.value, 0) },
      ]
    : categoryDataRaw;

  // Approval wait by workflow
  const approvalWaitData = (data.approval_wait_by_workflow || []).map(a => ({
    name: a.workflow_name,
    avg: Math.round(a.avg_wait_sec),
  }));

  return (
    <div className="space-y-6">
      {/* Hero stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard label="Executions (30d)" value={data.total_executions_30d.toLocaleString()} icon={Zap} />
        <StatCard label="Success Rate" value={`${Math.round(data.success_rate)}%`} subtext="last 30 days" icon={TrendingUp} />
        <StatCard label="Avg Duration" value={formatDuration(data.mean_duration_sec)} subtext="completed runs" icon={Clock} />
        <StatCard label="Avg Approval Wait" value={formatDuration(data.mean_approval_wait_sec)} icon={ShieldCheck} />
        <StatCard label="Active Workflows" value={data.active_workflows.toString()} icon={Activity} />
      </div>

      {/* Empty state when no executions exist */}
      {data.total_executions_30d === 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <ChartCard title="Execution Volume (30d)">
            <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">
              No workflow executions yet. Activate a workflow and trigger it to see execution data here.
            </div>
          </ChartCard>
          <ChartCard title="Executions by Workflow">
            <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">
              Workflow execution breakdown will appear here after your first run.
            </div>
          </ChartCard>
          <ChartCard title="Integration Usage">
            <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">
              See which integrations your workflows use most once executions start flowing.
            </div>
          </ChartCard>
          <ChartCard title="Approval Wait Time">
            <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">
              Approval gate metrics will appear here for workflows with approval steps.
            </div>
          </ChartCard>
        </div>
      )}

      {/* Execution volume over time (multi-line per workflow) */}
      {dailyChartData.length > 0 && (
        <ChartCard title="Execution Volume by Workflow (30d)">
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyChartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                {uniqueWorkflowIds.map((wfId, i) => (
                  <Area
                    key={wfId}
                    type="monotone"
                    dataKey={wfId}
                    name={workflowNames.get(wfId) || wfId}
                    stroke={COLORS[i % COLORS.length]}
                    fill={COLORS[i % COLORS.length]}
                    fillOpacity={0.1}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      )}

      {/* Per-workflow breakdown + Failure rate */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {perWorkflowBarData.length > 0 && (
          <ChartCard title="Executions by Workflow" className={failureRateData.length === 0 ? 'lg:col-span-2' : undefined}>
            <div style={{ height: Math.max(200, perWorkflowBarData.length * 36) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={perWorkflowBarData} layout="vertical" margin={{ top: 0, right: 16, left: 16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={<TruncatedTick width={160} />} stroke="hsl(var(--muted-foreground))" width={160} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="completed" stackId="a" fill="#22c55e" name="Completed" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="failed" stackId="a" fill="#ef4444" name="Failed" />
                  <Bar dataKey="waiting" stackId="a" fill="#f59e0b" name="Waiting" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        )}

        {failureRateData.length > 0 && (
          <ChartCard title="Failure Rate by Workflow (%)" className={perWorkflowBarData.length === 0 ? 'lg:col-span-2' : undefined}>
            <div style={{ height: Math.max(200, failureRateData.length * 36) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={failureRateData} layout="vertical" margin={{ top: 0, right: 16, left: 16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" domain={[0, 100]} />
                  <YAxis type="category" dataKey="name" tick={<TruncatedTick width={160} />} stroke="hsl(var(--muted-foreground))" width={160} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="rate" fill="#ef4444" name="Failure %" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        )}
      </div>

      {/* Duration by workflow */}
      {durationData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {(() => {
            const PAGE_SIZE = 5;
            const totalPages = Math.ceil(durationData.length / PAGE_SIZE);
            const pageData = durationData.slice(durationPage * PAGE_SIZE, (durationPage + 1) * PAGE_SIZE);
            return (
            <ChartCard title="Execution Duration by Workflow" className={(data.slowest_steps || []).length === 0 ? 'lg:col-span-2' : undefined}>
              <div style={{ height: Math.max(200, pageData.length * 36) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pageData} layout="vertical" margin={{ top: 0, right: 16, left: 16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v: number) => formatDuration(v)} />
                    <YAxis type="category" dataKey="name" tick={<TruncatedTick width={140} />} stroke="hsl(var(--muted-foreground))" width={140} />
                    <Tooltip content={<DurationTooltip />} />
                    <Bar dataKey="avg" fill="#3b82f6" name="Average" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="p95" fill="#8b5cf6" name="P95" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-3 pt-3 border-t">
                  <span className="text-[11px] text-muted-foreground">Page {durationPage + 1} of {totalPages}</span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setDurationPage(p => Math.max(0, p - 1))}
                      disabled={durationPage === 0}
                      className="text-xs px-2 py-1 rounded hover:bg-muted disabled:opacity-40"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setDurationPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={durationPage >= totalPages - 1}
                      className="text-xs px-2 py-1 rounded hover:bg-muted disabled:opacity-40"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </ChartCard>
            );
          })()}

          {/* Slowest steps */}
          {(data.slowest_steps || []).length > 0 && (
            <SlowestStepsPaginated steps={data.slowest_steps} />
          )}
        </div>
      )}

      {/* Integration analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {integrationData.length > 0 && (() => {
          const PAGE_SIZE = 5;
          const totalPages = Math.ceil(integrationData.length / PAGE_SIZE);
          const pageData = integrationData.slice(integrationPage * PAGE_SIZE, (integrationPage + 1) * PAGE_SIZE);
          return (
          <ChartCard title="Integration Usage" className={triggerSourceData.length === 0 && categoryData.length === 0 ? 'lg:col-span-2' : undefined}>
            <div style={{ height: Math.max(200, pageData.length * 40) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pageData} layout="vertical" margin={{ top: 0, right: 16, left: 16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={<TruncatedTick width={100} />} stroke="hsl(var(--muted-foreground))" width={100} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="#3b82f6" name="Invocations" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-3 pt-3 border-t">
                <span className="text-[11px] text-muted-foreground">Page {integrationPage + 1} of {totalPages}</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setIntegrationPage(p => Math.max(0, p - 1))}
                    disabled={integrationPage === 0}
                    className="text-xs px-2 py-1 rounded hover:bg-muted disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setIntegrationPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={integrationPage >= totalPages - 1}
                    className="text-xs px-2 py-1 rounded hover:bg-muted disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </ChartCard>
          );
        })()}

        {(triggerSourceData.length > 0 || categoryData.length > 0) && (
          <ChartCard title="Trigger Sources & Request Categories" className={integrationData.length === 0 ? 'lg:col-span-2' : undefined}>
            <div className="flex gap-8 py-2">
              {triggerSourceData.length > 0 && (
                <div className="flex-1 flex flex-col items-center gap-2">
                  <div className="text-[11px] font-medium text-muted-foreground">Triggers</div>
                  <div className="w-full" style={{ height: 160 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={triggerSourceData} dataKey="value" cx="50%" cy="50%" innerRadius={35} outerRadius={65} paddingAngle={2}>
                          {triggerSourceData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center">
                    {triggerSourceData.map((t, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-xs">
                        <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                        <span className="text-muted-foreground">{t.name}</span>
                        <span className="font-medium">{t.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {triggerSourceData.length > 0 && categoryData.length > 0 && (
                <div className="w-px bg-border" />
              )}
              {categoryData.length > 0 && (
                <div className="flex-1 flex flex-col items-center gap-2">
                  <div className="text-[11px] font-medium text-muted-foreground">Requests</div>
                  <div className="w-full" style={{ height: 160 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={categoryData} dataKey="value" cx="50%" cy="50%" innerRadius={35} outerRadius={65} paddingAngle={2}>
                          {categoryData.map((_, i) => <Cell key={i} fill={COLORS[(i + 3) % COLORS.length]} />)}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center">
                    {categoryData.map((c, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-xs">
                        <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: COLORS[(i + 3) % COLORS.length] }} />
                        <span className="text-muted-foreground">{c.name}</span>
                        <span className="font-medium">{c.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </ChartCard>
        )}
      </div>

      {/* Top Actions table */}
      {(data.top_actions || []).length > 0 && (
        <ActionLeaderboardPaginated actions={data.top_actions} isDark={isDark} />
      )}

      {/* Approval + Request analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {approvalWaitData.length > 0 && (
          <ChartCard title="Approval Wait Time by Workflow" className={approvalOutcomeData.length === 0 ? 'lg:col-span-2' : undefined}>
            <div style={{ height: Math.max(180, approvalWaitData.length * 36) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={approvalWaitData} layout="vertical" margin={{ top: 0, right: 16, left: 16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v: number) => formatDuration(v)} />
                  <YAxis type="category" dataKey="name" tick={<TruncatedTick width={160} />} stroke="hsl(var(--muted-foreground))" width={160} />
                  <Tooltip content={<DurationTooltip />} />
                  <Bar dataKey="avg" fill="#f59e0b" name="Avg Wait" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        )}

        {approvalOutcomeData.length > 0 && (
          <ChartCard title="Approval Outcomes Over Time" className={approvalWaitData.length === 0 ? 'lg:col-span-2' : undefined}>
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={approvalOutcomeData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="approved" fill="#22c55e" fillOpacity={0.15} stroke="#22c55e" strokeWidth={2} name="Approved" />
                  <Area type="monotone" dataKey="rejected" fill="#ef4444" fillOpacity={0.15} stroke="#ef4444" strokeWidth={2} name="Rejected" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        )}
      </div>

      {/* Requests over time */}
      {requestData.length > 0 && (
        <ChartCard title="Workflow Requests Over Time (by Source)">
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={requestData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="Slack" fill="#8b5cf6" fillOpacity={0.15} stroke="#8b5cf6" strokeWidth={2} />
                <Area type="monotone" dataKey="CLI" fill="#06b6d4" fillOpacity={0.15} stroke="#06b6d4" strokeWidth={2} />
                <Area type="monotone" dataKey="Platform" fill="#10b981" fillOpacity={0.15} stroke="#10b981" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      )}
    </div>
  );
};

const STEPS_PER_PAGE = 5;

function SlowestStepsPaginated({ steps }: { steps: WorkflowObservabilityData['slowest_steps'] }) {
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(steps.length / STEPS_PER_PAGE);
  const pageSteps = steps.slice(page * STEPS_PER_PAGE, (page + 1) * STEPS_PER_PAGE);
  const maxAvg = steps[0]?.avg_sec || 1;

  return (
    <ChartCard title="Slowest Steps (Avg Duration)">
      <div className="space-y-2">
        {pageSteps.map((step, i) => (
          <div key={page * STEPS_PER_PAGE + i} className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium truncate">{formatInternalName(step.step_action)}</span>
                <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{formatInternalName(step.integration)}</span>
              </div>
              <div className="mt-1 h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-blue-500"
                  style={{ width: `${Math.min(100, (step.avg_sec / maxAvg) * 100)}%` }}
                />
              </div>
            </div>
            <div className="text-right shrink-0 w-16">
              <div className="text-xs font-semibold">{formatDuration(step.avg_sec)}</div>
              <div className="text-[10px] text-muted-foreground">{step.count} runs</div>
            </div>
          </div>
        ))}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 pt-3 border-t">
          <span className="text-[11px] text-muted-foreground">Page {page + 1} of {totalPages}</span>
          <div className="flex gap-1">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="text-xs px-2 py-1 rounded hover:bg-muted disabled:opacity-40"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="text-xs px-2 py-1 rounded hover:bg-muted disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </ChartCard>
  );
}

const ACTIONS_PER_PAGE = 10;

function ActionLeaderboardPaginated({ actions, isDark }: { actions: WorkflowObservabilityData['top_actions']; isDark: boolean }) {
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(actions.length / ACTIONS_PER_PAGE);
  const pageActions = actions.slice(page * ACTIONS_PER_PAGE, (page + 1) * ACTIONS_PER_PAGE);

  return (
    <ChartCard title="Action Leaderboard">
      <div className="rounded-lg border overflow-x-auto md:overflow-visible">
        <table className="w-full text-sm min-w-[640px] md:min-w-0">
          <thead>
            <tr className="border-b">
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Action</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Integration</th>
              <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Invocations</th>
              <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Successes</th>
              <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Failures</th>
              <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Error Rate</th>
              <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Avg Duration</th>
            </tr>
          </thead>
          <tbody>
            {pageActions.map((a, i) => (
              <tr key={page * ACTIONS_PER_PAGE + i} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                <td className="px-4 py-2.5 font-medium">{formatInternalName(a.action)}</td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <IntegrationIcon integration={a.integration} isDark={isDark} />
                    <span className="text-muted-foreground">{formatInternalName(a.integration)}</span>
                  </div>
                </td>
                <td className="px-4 py-2.5 text-right">{a.invocations}</td>
                <td className="px-4 py-2.5 text-right text-green-600">{a.successes}</td>
                <td className="px-4 py-2.5 text-right text-red-500">{a.failures}</td>
                <td className="px-4 py-2.5 text-right">
                  <span className={errorRateColor(a.error_rate)}>
                    {a.error_rate.toFixed(1)}%
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right text-muted-foreground">{formatDuration(a.avg_sec)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 pt-3 border-t">
          <span className="text-[11px] text-muted-foreground">
            Page {page + 1} of {totalPages}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="text-xs px-2 py-1 rounded hover:bg-muted disabled:opacity-40"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="text-xs px-2 py-1 rounded hover:bg-muted disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </ChartCard>
  );
}

export default WorkflowObservability;
